import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Hospital } from 'iconsax-react';

/**
 * The frame around every unauthenticated screen.
 *
 * Deliberately plain: a centred card on the app background, same tokens as the
 * rest of the portal. The four legacy apps each had their own login design —
 * different banners, different layouts — which is exactly the drift this
 * replaces.
 */
export function AuthLayout({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Stack gap={2.5} sx={{ width: '100%', maxWidth: 420 }}>
        <Stack direction="row" alignItems="center" gap={1.25} sx={{ px: 0.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: `${theme.custom.radius.md}px`,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Hospital size={20} color="currentColor" />
          </Box>
          <Typography variant="h3">Radiology Portal</Typography>
        </Stack>

        <Card sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {description}
            </Typography>
          )}
          {children}
        </Card>

        {footer && <Box sx={{ px: 0.5 }}>{footer}</Box>}
      </Stack>
    </Box>
  );
}
